import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import PopulateLookup from 'core/components/Lookup';
import Tag from 'core/components/Tag';
import Pill from 'core/components/Pill';
import IconButton from 'core/components/IconButton';
import EyeIcon from 'core/icons/eye';
import PrinterIcon from 'core/icons/printer';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  FormViewDTO,
  FormWorkflowFormStatusDTO,
} from 'dtos';
import {
  FormCategory,
} from 'dtos/formCategory';
import {
  getApiFormGetFormPreviewForPatientGetFormPreviewForPatientFormIdPatientId,
} from 'endpoints';
import {
  getFormOptions,
} from 'views/EMR/Settings/transport';
import {
  usePrintMediaRequest,
} from 'views/EMR/hooks/media';
import PdfTemplateBuilder from 'views/EMR/Settings/PdfTemplateBuilder';
import {
  getFormWorkflowStatusLabel,
  getFormWorkflowStatusTooltip,
} from 'views/EMR/components/FormWorkflow/utils';
import {
  isEmptyString,
  isNilOrZero,
} from '@populateui/utils';

type Props = {
  category: FormCategory;
  patientId?: number;
  value?: FormViewDTO[];
  onChange: (forms: FormViewDTO[]) => void;
  formStatusByFormId?: Map<number, FormWorkflowFormStatusDTO | undefined>;
  showStatus?: boolean;
};

function getFormUniqueId(form: FormViewDTO) {
  return String(form.formId ?? form.title ?? '');
}

export default function StartFormWorkflowFormPicker({
  category,
  patientId,
  value = [],
  onChange,
  formStatusByFormId,
  showStatus = false,
}: Props) {
  const [previewFormId, setPreviewFormId] = useState<number | null>(null);
  const [printingFormId, setPrintingFormId] = useState<number | null>(null);
  const {
    print,
    isPrinting,
  } = usePrintMediaRequest<[number, number]>(
    getApiFormGetFormPreviewForPatientGetFormPreviewForPatientFormIdPatientId,
  );

  useEffect(() => {
    if (!isPrinting) {
      setPrintingFormId(null);
    }
  }, [isPrinting]);

  const handleRemoveForm = useCallback((formId?: string | null) => {
    if (isEmptyString(formId ?? '')) {
      return;
    }

    onChange(value.filter((form) => String(form.formId ?? '') !== formId));
  }, [onChange, value]);

  const handlePreviewForm = useCallback((
    event: MouseEvent<HTMLButtonElement>,
    formId?: number | null,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    if (!isNilOrZero(formId)) {
      setPreviewFormId(formId ?? 0);
    }
  }, []);

  const handlePrintForm = useCallback((
    event: MouseEvent<HTMLButtonElement>,
    formId?: number | null,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    if (!isNilOrZero(formId) && !isNilOrZero(patientId)) {
      setPrintingFormId(formId ?? 0);
      print([formId ?? 0, patientId ?? 0]);
    }
  }, [patientId, print]);

  return (
    <>
      <PopulateLookup<FormViewDTO>
        id="form-multi-select"
        multiple
        value={value}
        onChange={(forms) => onChange(forms as FormViewDTO[])}
        disableRemove={false}
        getOptions={getFormOptions}
        queryOptions={{ category, includeDrafts: false }}
        dataKey={`form-multi-select-${category}`}
        placeholder="Search for forms"
        emptyText="No forms found"
        minSearchSize={0}
        openOnLoad={false}
        disableCloseOnSelect
        className="!w-full"
        getUniqueId={getFormUniqueId}
        getLabel={(form) => form.title ?? ''}
        isOptionEqualToValue={(option, selected) => (
          option?.formId === (selected as FormViewDTO)?.formId
        )}
        customFilterOptions={(options) =>
          options.filter(
            (form) => !value.some((selected) => selected.formId === form.formId),
          )
        }
        classes={{
          listbox: '!max-h-56',
          dropdownContainer: '!w-full',
        }}
        renderTags={(forms) => (
          <div className="flex flex-wrap gap-1">
            {forms.map((form) => (
              <Pill
                key={getFormUniqueId(form)}
                id={getFormUniqueId(form)}
                label={form.title ?? ''}
                onRemove={handleRemoveForm}
                classes={{
                  root: '!h-4 !px-2',
                  label: 'max-w-[280px]',
                }}
              />
            ))}
          </div>
        )}
        renderOptionRow={(form) => {
          const formStatus = formStatusByFormId?.get(form.formId ?? 0);
          const isPrintingForm = isPrinting && printingFormId === form.formId;

          return (
            <div className="group flex w-full min-w-0 items-center gap-3">
              <span className="min-w-0 flex-1 truncate text-left font-semibold">
                {form.title ?? '--'}
              </span>
              {showStatus && (
                <Tag
                  tagName={getFormWorkflowStatusLabel(formStatus?.status)}
                  tooltip={getFormWorkflowStatusTooltip(formStatus?.status, formStatus?.statusAt)}
                  defaultColor="bg-gray-200 text-gray-600"
                  className="shrink-0 whitespace-nowrap !font-semibold"
                />
              )}
              <IconButton
                tooltip="Print"
                size="small"
                disabled={isNilOrZero(patientId) || isPrinting}
                className="!p-1 shrink-0 text-gray-400 hover:text-gray-600"
                onClick={(event) => { handlePrintForm(event, form.formId); }}
              >
                {isPrintingForm ? (
                  <Spinner className="!w-4 !h-4" />
                ) : (
                  <PrinterIcon className="!w-4 !h-4" />
                )}
              </IconButton>
              <IconButton
                tooltip="Preview"
                size="small"
                className="!p-1 shrink-0 text-gray-400 hover:text-gray-600"
                onClick={(event) => { handlePreviewForm(event, form.formId); }}
              >
                <EyeIcon className="!w-4 !h-4" />
              </IconButton>
            </div>
          );
        }}
      />

      {!isNil(previewFormId) && (
        <PdfTemplateBuilder
          formId={previewFormId}
          onClose={() => { setPreviewFormId(null); }}
        />
      )}
    </>
  );
}
